import React, { useState, useContext, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { Vector3, Frustum } from 'three';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { DesignContext } from '../contexts/designContext.js';
import { SHAPE_X_MARKER_IMG_PX } from '../constants/shapeCrossMarkerDataUrl.js';
import {
  TEXT_ANNOTATION_MARKER_IMAGE_DATA_URL,
  TEXT_ANNOTATION_MARKER_IMG_PX
} from '../constants/text-annotation-marker-data-url.js';

import { TbSunElectricity } from 'react-icons/tb/index.js';
import { LiaYCombinator } from 'react-icons/lia/index.js';
import { MdOutlineMedicalServices } from 'react-icons/md/index.js';
import { LuThermometerSun, LuUtilityPole, LuGalleryHorizontal } from 'react-icons/lu/index.js';
import { AiOutlineQuestionCircle, AiOutlineDisconnect } from 'react-icons/ai/index.js';
import { IoBatteryFull } from 'react-icons/io5/index.js';

import { TextAnnotationPopup } from '../components/TextAnnotationPopup.js';
import { intersectRaycastableObjects } from '../algorithms/utils.js';
import { registerOptionalTextAnnotationPopup } from '../utils/registerOptionalTextAnnotationPopup.js';

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
const pointGeometry = new THREE.SphereGeometry(1.0, 16, 16);
const pointMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
const annotationPointer = new THREE.Mesh(pointGeometry, pointMaterial);
annotationPointer.name = 'annotationPointer';
var mouseDownPosition = null;

const type_mapping = {
  custom: 'Custom',
  inverter: 'Inverter',
  combiner_panel: 'Combiner Panel',
  service_panel: 'Service Panel',
  production_meter: 'Production Meter',
  utility_meter: 'Utility Meter',
  disconnect: 'Disconnect',
  sub_panel: 'Sub Panel'
};

const type_color_mapping = {
  Custom: 'blue',
  Inverter: 'brown',
  'Combiner Panel': 'green',
  'Service Panel': 'yellow',
  'Production Meter': 'red',
  'Utility Meter': 'purple',
  Disconnect: 'orange',
  'Sub Panel': 'pink',
  Battery: '#b8860b'
};

const LINE_PICK_SQ = 2.5 * 2.5;
const POINT_PICK_SQ = 4.5 * 4.5;
const CIRCLE_MARGIN = 1.5;

function distPointToSegmentSq(px, py, pz, ax, ay, az, bx, by, bz) {
  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const apx = px - ax;
  const apy = py - ay;
  const apz = pz - az;
  const abLenSq = abx * abx + aby * aby + abz * abz;
  const t = abLenSq < 1e-12 ? 0 : Math.max(0, Math.min(1, (apx * abx + apy * aby + apz * abz) / abLenSq));
  const cx = ax + abx * t;
  const cy = ay + aby * t;
  const cz = az + abz * t;
  const dx = px - cx;
  const dy = py - cy;
  const dz = pz - cz;
  return dx * dx + dy * dy + dz * dz;
}

function geomXYZ(g) {
  if (!g) return null;
  if (Array.isArray(g)) {
    if (g.length >= 3) return { x: g[0], y: g[1], z: g[2] };
    return { x: g[0], y: 0, z: g[1] };
  }
  return {
    x: g.x,
    y: typeof g.y === 'number' ? g.y : 0,
    z: typeof g.z === 'number' ? g.z : 0
  };
}

/** Ray-cast in XZ (roof plane) so driveway delete works when clicking inside the polygon, not only on edges. */
function pointInPolygonXZ(px, pz, verts) {
  if (!verts || verts.length < 3) return false;
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const xi = verts[i].x;
    const zi = verts[i].z;
    const xj = verts[j].x;
    const zj = verts[j].z;
    const denom = zj - zi;
    if (Math.abs(denom) < 1e-9) continue;
    if (zi > pz !== zj > pz && px < ((xj - xi) * (pz - zi)) / denom + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * When Annotations should own canvas pointer events. Meter/driveway/wiring geometry is drawn by
 * Scene `Drawing` — only hook pointer + mouse for **delete** there so we never stopPropagation
 * during draw/edit (DesignTool + designer). Plain `annotations` viewState keeps legacy behavior
 * for markers, text placement, and delete.
 */
function shouldAnnotationsComponentHandlePointer(viewState, mode) {
  if (mode === '') return false;
  if (viewState === 'annotations') return true;
  return (viewState === 'meter' || viewState === 'driveway' || viewState === 'wiring') && mode === 'delete';
}

/**
 * Pick annotation under click (roof hit point). Returns closest hit by type-specific thresholds.
 */
function pickAnnotationToDelete(clickPoint, annotations) {
  if (!clickPoint || !annotations?.length) return null;
  const px = clickPoint.x;
  const py = clickPoint.y;
  const pz = clickPoint.z;
  let best = null;
  let bestMetric = Infinity;

  const consider = (ann, metricSq) => {
    if (metricSq < bestMetric) {
      bestMetric = metricSq;
      best = ann;
    }
  };

  for (const ann of annotations) {
    if (!ann) continue;

    if (ann.type === 'ShapeLine' || ann.shape === 'line' || ann.shape === 'Line') {
      if (ann.geometry?.length >= 2) {
        const g0 = geomXYZ(ann.geometry[0]);
        const g1 = geomXYZ(ann.geometry[1]);
        if (g0 && g1) {
          const dSq = distPointToSegmentSq(px, py, pz, g0.x, g0.y, g0.z, g1.x, g1.y, g1.z);
          if (dSq <= LINE_PICK_SQ) consider(ann, dSq);
        }
      }
      continue;
    }

    if (ann.type === 'ShapeCircle' || ann.shape === 'circle') {
      if (ann.position != null && ann.radius != null) {
        const c = ann.position;
        const d = Math.sqrt((px - c.x) ** 2 + (py - c.y) ** 2 + (pz - c.z) ** 2);
        if (d <= ann.radius + CIRCLE_MARGIN) consider(ann, d * d);
      }
      continue;
    }

    if (ann.type === 'ShapeRectangle' || ann.shape === 'rectangle') {
      if (ann.position != null && ann.width != null && ann.height != null) {
        const cx = ann.position.x;
        const cz = ann.position.z;
        const cy = ann.position.y;
        const hw = ann.width / 2;
        const hh = ann.height / 2;
        if (px >= cx - hw && px <= cx + hw && pz >= cz - hh && pz <= cz + hh) {
          const dSq = (px - cx) ** 2 + (py - cy) ** 2 + (pz - cz) ** 2;
          consider(ann, dSq);
        }
      }
      continue;
    }

    if (ann.type === 'ShapeX' || ann.shape === 'x') {
      if (ann.position != null) {
        const c = ann.position;
        const r = ann.radius ?? 2;
        const dSq = (px - c.x) ** 2 + (py - c.y) ** 2 + (pz - c.z) ** 2;
        if (dSq <= (r + CIRCLE_MARGIN) ** 2) consider(ann, dSq);
      }
      continue;
    }

    if (Array.isArray(ann.geometry) && ann.geometry.length >= 1) {
      if (ann.geometry.length === 1) {
        const g0 = geomXYZ(ann.geometry[0]);
        if (g0) {
          const dSq = (px - g0.x) ** 2 + (py - g0.y) ** 2 + (pz - g0.z) ** 2;
          if (dSq <= POINT_PICK_SQ) consider(ann, dSq);
        }
      } else {
        let minSegSq = Infinity;
        for (let i = 0; i < ann.geometry.length - 1; i++) {
          const g0 = geomXYZ(ann.geometry[i]);
          const g1 = geomXYZ(ann.geometry[i + 1]);
          if (!g0 || !g1) continue;
          minSegSq = Math.min(minSegSq, distPointToSegmentSq(px, py, pz, g0.x, g0.y, g0.z, g1.x, g1.y, g1.z));
        }
        if (minSegSq > LINE_PICK_SQ && ann.objectType === 'Driveway' && ann.geometry.length >= 3) {
          const verts = ann.geometry.map((v) => geomXYZ(v)).filter(Boolean);
          if (verts.length >= 3 && pointInPolygonXZ(px, pz, verts)) {
            minSegSq = 0;
          }
        }
        if (minSegSq <= LINE_PICK_SQ) consider(ann, minSegSq);
      }
      continue;
    }

    if (ann.position != null) {
      const c = geomXYZ(ann.position);
      if (!c) continue;
      const dSq = (px - c.x) ** 2 + (py - c.y) ** 2 + (pz - c.z) ** 2;
      if (dSq <= POINT_PICK_SQ) consider(ann, dSq);
    }
  }

  return best;
}

function filterOutAnnotation(annotations, hit) {
  if (!Array.isArray(annotations) || !hit) return annotations;
  return annotations.filter((a) => (hit.id != null ? a?.id !== hit.id : a !== hit));
}

const Annotations = ({ canvas, controls, enabled }) => {
  const {
    accordion,
    viewState,
    mode,
    annotations,
    setAnnotations,
    newAnnotation,
    iframeCallback,
    designFrame,
    readonly,
    annotationsNeutralPlacementCursor,
    onTextAnnotationRequest, // 🆕 Optional callback for text annotation popup
    userName, // 🆕 Optional user name for text annotation popup
    showTextAnnotationPopup, // 🆕 Optional callback to show text annotation popup with data
    onOpenImageAnnotation, // 🆕 Optional callback to open image annotation modal
    onOpenCameraCapture, // 🆕 Optional callback to open camera capture modal
    uploadAnnotationFile, // 🆕 Optional upload for annotation popup image/video (storage)
    imageAnnotationModalOpen = false, // 🆕 When true (image annotator open), hide point labels so they don't overlap
    invokeShapeAnnotateToolbarSessionEnd, // 🆕 Designer: end shape-toolbar session after placement popup (optional)
    shapePopupHideTaskCheckbox = false // 🆕 Checklist toolbar: hide Task on shape + Text placement popups
  } = useContext(DesignContext);

  // 🆕 State for text annotation popup
  const [textPopupPosition, setTextPopupPosition] = useState(null);
  const [textPopupInitialData, setTextPopupInitialData] = useState(null); // 🆕 Initial data for popup (for editing)
  const [textPopupReadOnly, setTextPopupReadOnly] = useState(false); // 🆕 Whether popup is read-only (for viewing from History)
  const [textPopupIsFromTask, setTextPopupIsFromTask] = useState(false); // 🆕 Whether popup is opened from Tasks tab
  const [textPopupOnTaskComplete, setTextPopupOnTaskComplete] = useState(null); // 🆕 Callback for task completion
  const [textPopupOnCancel, setTextPopupOnCancel] = useState(null); // 🆕 Callback for cancel (for shape annotations)
  /** True when popup was opened from shape placement (CanvasShapeAnnotations passes onCancel) — save/cancel should deselect shape tool */
  const [textPopupEndsShapeToolSession, setTextPopupEndsShapeToolSession] = useState(false);
  /** Checklist shape toolbar: hide Task checkbox on placement form */
  const [textPopupHideTaskCheckbox, setTextPopupHideTaskCheckbox] = useState(false);

  // 🔧 Function to close text popup (for image annotation modal)
  const closeTextPopup = useCallback(() => {
    setTextPopupPosition(null);
    setTextPopupInitialData(null);
    setTextPopupReadOnly(false);
    setTextPopupIsFromTask(false);
    setTextPopupOnTaskComplete(null);
    setTextPopupHideTaskCheckbox(false);
  }, []);

  // 🆕 Listen for external requests to show text annotation popup (e.g., from History click)
  useEffect(() => {
    const handler = (data) => {
      const {
        position: popupPosition,
        annotationData,
        readOnly = false,
        isFromTask = false,
        onTaskComplete = null,
        onCancel = null, // 🆕 Cancel callback for shape annotations
        hideTaskCheckbox = false
      } = data;
      console.log('📝 Annotations - External request to show text popup:', {
        position: popupPosition,
        annotationData,
        readOnly,
        isFromTask,
        hasOnTaskComplete: !!onTaskComplete,
        hasOnCancel: !!onCancel,
        hideTaskCheckbox,
        hasGeometry: !!annotationData?.geometry,
        hasShape: !!annotationData?.shape,
        hasColor: !!annotationData?.color,
        geometry: annotationData?.geometry,
        shape: annotationData?.shape,
        color: annotationData?.color
      });
      setTextPopupPosition(popupPosition);
      setTextPopupInitialData(annotationData || null);
      setTextPopupReadOnly(readOnly); // Set read-only mode
      setTextPopupIsFromTask(isFromTask); // 🆕 Set isFromTask flag
      setTextPopupOnTaskComplete(() => onTaskComplete); // 🆕 Store task completion callback
      // 🆕 Store cancel callback if provided (for shape annotations)
      if (onCancel) {
        setTextPopupOnCancel(() => onCancel);
      } else {
        setTextPopupOnCancel(null);
      }
      setTextPopupEndsShapeToolSession(!!onCancel);
      setTextPopupHideTaskCheckbox(!!hideTaskCheckbox);
    };

    if (showTextAnnotationPopup) {
      console.log('📝 Annotations - Registering text annotation popup handler');
    }
    const unregister = registerOptionalTextAnnotationPopup(showTextAnnotationPopup, handler);
    if (!unregister) return undefined;

    return () => {
      console.log('📝 Annotations - Unregistering text annotation popup handler');
      unregister();
    };
  }, [showTextAnnotationPopup]);

  const { gl, camera, scene, size: canvasSize } = useThree();

  // Compute popup placement so it opens in the opposite direction when near viewport edges
  const popupPlacement = useMemo(() => {
    if (!textPopupPosition || !camera || !canvasSize?.width || !canvasSize?.height) {
      return { vertical: 'bottom', horizontal: 'right' };
    }
    const v = new THREE.Vector3(textPopupPosition.x, textPopupPosition.y, textPopupPosition.z).project(camera);
    const screenX = (v.x + 1) * 0.5 * canvasSize.width;
    const screenY = (1 - v.y) * 0.5 * canvasSize.height;
    const POPUP_WIDTH = 320;
    const POPUP_HEIGHT = 400;
    const MARGIN = 16;
    let vertical = 'bottom';
    let horizontal = 'right';
    if (screenY + POPUP_HEIGHT > canvasSize.height - MARGIN) vertical = 'top';
    else if (screenY - POPUP_HEIGHT < MARGIN) vertical = 'bottom';
    if (screenX + POPUP_WIDTH > canvasSize.width - MARGIN) horizontal = 'left';
    else if (screenX - POPUP_WIDTH < MARGIN) horizontal = 'right';
    return { vertical, horizontal };
  }, [textPopupPosition, camera, canvasSize?.width, canvasSize?.height]);

  // const rearTarget = useRef();
  // const [annotationTexture, setAnnotationTexture] = useState()
  // useLayoutEffect(() => {
  //   try {
  //     new THREE.TextureLoader().load("/aerialytic-logo.png", (tx) => {
  //       setAnnotationTexture(tx);
  //     });
  //   } catch { console.log('error in load annotation texture'); }
  // }, []);

  const get3dPosition = (e) => {
    mouse.set((e.offsetX / canvasSize.width) * 2 - 1, -(e.offsetY / canvasSize.height) * 2 + 1, 0.5);
    raycaster.setFromCamera(mouse, camera);
    // Recurse into groups (roof/ground) and skip sky — non-recursive hits the
    // sky sphere (~Y±600), which looks fine in ortho 2D but shifts pins in 3D.
    const intersects = intersectRaycastableObjects(raycaster, scene.children, true);
    const isSkippable = (obj) =>
      !obj || !obj.visible || obj.name === 'annotationPointer' || obj.name === 'sky' || obj.name === 'wallBorder';

    let fallback = null;
    for (let i = 0; i < intersects.length; i++) {
      const intersect = intersects[i];
      if (isSkippable(intersect.object)) continue;
      let ratio = 1.0;
      if (camera.type === 'PerspectiveCamera')
        ratio = Math.max(0.1, Math.min(1.0, camera.position.length() / 100)).toFixed(2);
      else ratio = Math.max(0.2, Math.min(1.0, 4.0 / camera.zoom)).toFixed(2);
      // Prefer roof/ground so markers land on the design surface.
      if (intersect.object.name === 'roofPlane' || intersect.object.name === 'groundPlane') {
        return [intersect.point, ratio];
      }
      if (!fallback) fallback = [intersect.point, ratio];
    }
    return fallback || [null, 1.0];
  };

  const canvasMouseDown = useCallback(
    (e) => {
      if (!enabled || !shouldAnnotationsComponentHandlePointer(viewState, mode)) return;

      e.preventDefault();
      e.stopPropagation();

      const [point, ratio] = get3dPosition(e);
      mouseDownPosition = point;
    },
    [controls, canvas, mouse, canvasSize.width, canvasSize.height, camera, scene, viewState, mode, enabled]
  );

  const canvasMouseUp = useCallback(
    (e) => {
      if (!enabled || !shouldAnnotationsComponentHandlePointer(viewState, mode) || !mouseDownPosition) return;

      e.preventDefault();
      e.stopPropagation();

      const [point, ratio] = get3dPosition(e);
      const dist = mouseDownPosition.distanceTo(point);

      // Delete mode: remove closest annotation under click (same UX as segments/obstacles/trees)
      if (
        mode === 'delete' &&
        dist <= 1 &&
        !readonly &&
        (accordion === 'annotations' || viewState === 'annotations') &&
        setAnnotations &&
        annotations &&
        annotationPointer?.position
      ) {
        const hit = pickAnnotationToDelete(annotationPointer.position, annotations);
        if (hit) {
          console.log('🗑️ Annotations - Delete mode: removing annotation', hit.id);
          setAnnotations(filterOutAnnotation(annotations, hit));
        }
        return;
      }

      // 🆕 Text annotation: open text popup at click position (point-annotation with popup)
      // Use the mouseup raycast hit (same as CanvasShapeAnnotations), not annotationPointer —
      // pointer state is only updated on mousemove, so a click-without-move can open far from the click.
      if (mode === 'text' && dist <= 1) {
        if (!point) return;
        setTextPopupPosition({
          x: point.x,
          y: point.y,
          z: point.z
        });
        setTextPopupInitialData(null);
        setTextPopupReadOnly(false);
        setTextPopupIsFromTask(false);
        setTextPopupOnTaskComplete(null);
        setTextPopupOnCancel(null);
        setTextPopupEndsShapeToolSession(false);
        setTextPopupHideTaskCheckbox(!!shapePopupHideTaskCheckbox);
        return;
      }

      if (
        newAnnotation &&
        dist <= 1 &&
        (mode in type_mapping || (viewState === 'annotations' && iframeCallback && !designFrame))
      )
        newAnnotation(annotationPointer.position, type_mapping[mode]);
    },
    [
      controls,
      canvas,
      mouse,
      canvasSize.width,
      canvasSize.height,
      camera,
      scene,
      viewState,
      mode,
      enabled,
      readonly,
      accordion,
      annotations,
      setAnnotations,
      shapePopupHideTaskCheckbox
    ]
  );

  const canvasMouseMove = useCallback(
    (e) => {
      if (!enabled || !shouldAnnotationsComponentHandlePointer(viewState, mode)) return;

      e.preventDefault();
      e.stopPropagation();

      const [point, ratio] = get3dPosition(e);
      if (point) {
        annotationPointer.position.copy(point);
        annotationPointer.scale.set(ratio, ratio, ratio);
      }
    },
    [controls, canvas, mouse, canvasSize.width, canvasSize.height, camera, scene, viewState, mode, enabled]
  );

  useEffect(() => {
    if (!canvas?.current) return;
    const el = canvas.current;
    if (!shouldAnnotationsComponentHandlePointer(viewState, mode)) {
      el.style.cursor = 'default';
      scene.remove(annotationPointer);
      return;
    }
    if (mode === 'delete') {
      el.style.cursor = 'pointer';
      scene.add(annotationPointer);
    } else if (annotationsNeutralPlacementCursor === true) {
      el.style.cursor = 'default';
      scene.remove(annotationPointer);
    } else {
      el.style.cursor = 'crosshair';
      scene.add(annotationPointer);
    }
  }, [viewState, mode, scene, canvas, annotationsNeutralPlacementCursor]);

  // useEffect(() => {
  //   scene.add(annotationPointer);
  //   return () => { scene.remove(annotationPointer); };
  // },[]);

  useEffect(() => {
    const canvasRef = canvas.current;
    if (canvasRef) {
      canvas.current.addEventListener('mousedown', canvasMouseDown, false);
      canvas.current.addEventListener('mouseup', canvasMouseUp, false);
      canvas.current.addEventListener('mousemove', canvasMouseMove, false);
    }
    return () => {
      if (canvasRef) {
        canvasRef.removeEventListener('mousedown', canvasMouseDown, false);
        canvasRef.removeEventListener('mouseup', canvasMouseUp, false);
        canvasRef.removeEventListener('mousemove', canvasMouseMove, false);
      }
    };
  }, [canvas, canvasMouseDown, canvasMouseMove, canvasMouseUp]);

  const getIcon = (type) => {
    // Designer stores display names; legacy switch used internal captions.
    const normalizedType =
      {
        'Main Service Panel': 'Service Panel',
        'AC Disconnect': 'Disconnect',
        'Prod Meter': 'Production Meter'
      }[type] || type;

    switch (normalizedType) {
      case 'Inverter':
        return (
          <TbSunElectricity
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Combiner Panel':
        return (
          <LiaYCombinator
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Service Panel':
        return (
          <MdOutlineMedicalServices
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Production Meter':
        return (
          <LuThermometerSun
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Utility Meter':
        return (
          <LuUtilityPole
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Disconnect':
        return (
          <AiOutlineDisconnect
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Sub Panel':
        return (
          <LuGalleryHorizontal
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Battery':
        return (
          <IoBatteryFull
            style={{
              color: type_color_mapping[normalizedType],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
      case 'Text':
        return null;
      default:
        return (
          <AiOutlineQuestionCircle
            style={{
              color: type_color_mapping['custom'],
              position: 'relative',
              top: '-2px',
              left: '1px'
            }}
          />
        );
    }
  };

  // 🆕 Handler to close text popup and create/update annotation
  const handleTextAnnotationSubmit = useCallback(
    (data) => {
      console.log('📝 Annotations - Text popup submit:', {
        data,
        textPopupPosition,
        isEditing: !!textPopupInitialData,
        isReadOnly: textPopupReadOnly,
        textPopupInitialData: textPopupInitialData,
        hasGeometry: !!textPopupInitialData?.geometry,
        hasShape: !!textPopupInitialData?.shape,
        hasColor: !!textPopupInitialData?.color,
        geometry: textPopupInitialData?.geometry,
        shape: textPopupInitialData?.shape,
        color: textPopupInitialData?.color
      });

      // Don't save if in read-only mode (viewing from History)
      if (textPopupReadOnly) {
        console.log('📝 Annotations - Read-only mode, closing popup without saving');
        setTextPopupPosition(null);
        setTextPopupInitialData(null);
        setTextPopupReadOnly(false);
        setTextPopupEndsShapeToolSession(false);
        setTextPopupOnCancel(null);
        setTextPopupHideTaskCheckbox(false);
        return;
      }

      // 🔧 Check if annotation exists in the array (by ID or position)
      // This determines whether we should update or create
      // 🆕 Prioritize ID matching for ShapeLine annotations to avoid duplicates
      let existingAnnotation = null;
      if (textPopupInitialData && textPopupPosition && annotations && Array.isArray(annotations)) {
        existingAnnotation = annotations.find((ann) => {
          // 🆕 Match by ID first (most reliable, especially for ShapeLine)
          if (ann.id && textPopupInitialData.id && ann.id === textPopupInitialData.id) {
            return true;
          }
          // Match by position (within tolerance) - only if no ID match
          if (
            ann.position &&
            Math.abs(ann.position.x - textPopupPosition.x) < 0.1 &&
            Math.abs(ann.position.y - textPopupPosition.y) < 0.1 &&
            Math.abs(ann.position.z - textPopupPosition.z) < 0.1
          ) {
            return true;
          }
          return false;
        });

        console.log('📝 Annotations - Looking for existing annotation:', {
          hasInitialData: !!textPopupInitialData,
          initialDataId: textPopupInitialData?.id,
          annotationsCount: annotations.length,
          found: !!existingAnnotation
        });
      }

      // If annotation exists, update it
      if (existingAnnotation && setAnnotations && annotations) {
        const updatedAnnotations = annotations.map((ann) => {
          // Find the annotation by position or ID
          if (
            (ann.position &&
              Math.abs(ann.position.x - textPopupPosition.x) < 0.1 &&
              Math.abs(ann.position.y - textPopupPosition.y) < 0.1 &&
              Math.abs(ann.position.z - textPopupPosition.z) < 0.1) ||
            (ann.id && textPopupInitialData.id && ann.id === textPopupInitialData.id)
          ) {
            // Update the annotation with new data
            // 🔧 Preserve type, shape, geometry, and color for ShapeLine annotations
            return {
              ...ann,
              text: data.text,
              message: data.message,
              isHidden: data.isHidden,
              isTask: data.isTask,
              taskStatus: data.isTask ? data.taskStatus || 'pending' : undefined,
              assignedTo: data.isTask ? data.assignedTo || 'designer' : undefined,
              date: data.date,
              imageUrl: data.imageUrl, // 🆕 Preserve imageUrl
              imageAnnotations: data.imageAnnotations, // 🆕 Preserve imageAnnotations
              // 🆕 Preserve shape-specific fields for ShapeLine, ShapeCircle, and ShapeRectangle annotations
              type: textPopupInitialData?.type || ann.type || 'Text',
              shape: textPopupInitialData?.shape || ann.shape,
              geometry: textPopupInitialData?.geometry || ann.geometry, // For lines
              radius: textPopupInitialData?.radius || ann.radius, // For circles
              width: textPopupInitialData?.width || ann.width, // For rectangles
              height: textPopupInitialData?.height || ann.height, // For rectangles
              color: textPopupInitialData?.color || ann.color,
              markerImage: textPopupInitialData?.markerImage ?? ann.markerImage,
              updatedAt: new Date().toISOString()
            };
          }
          return ann;
        });
        setAnnotations(updatedAnnotations);
        console.log('✅ Annotations - Updated existing text annotation');
      } else if (newAnnotation && textPopupPosition) {
        // Create new annotation (either no initialData, or initialData exists but annotation not in array)
        // 🔧 Use type from initialData if available (e.g., 'ShapeLine'), otherwise default to 'Text'
        const annotationType = textPopupInitialData?.type || 'Text';

        // 🆕 CRITICAL: Extract geometry, radius, width, height, shape, and color from initialData BEFORE building params
        // This ensures they're always included for ShapeLine, ShapeCircle, and ShapeRectangle annotations
        const geometry = textPopupInitialData?.geometry; // For lines
        const radius = textPopupInitialData?.radius; // For circles
        const width = textPopupInitialData?.width; // For rectangles
        const height = textPopupInitialData?.height; // For rectangles
        const shape = textPopupInitialData?.shape;
        const color = textPopupInitialData?.color;

        const params = {
          ...data,
          // 🔧 Preserve position and type from initialData if available
          ...(textPopupInitialData?.position && {
            position: textPopupInitialData.position
          }),
          ...(textPopupInitialData?.type && {
            type: textPopupInitialData.type
          }),
          ...(textPopupInitialData?.id && {
            id: textPopupInitialData.id
          }),
          ...(textPopupInitialData?.createdAt && {
            createdAt: textPopupInitialData.createdAt
          }),
          // 🆕 CRITICAL: Always include geometry (for lines), radius (for circles), width/height (for rectangles), shape, and color if they exist in initialData
          // This ensures ShapeLine, ShapeCircle, and ShapeRectangle annotations maintain their visual representation
          ...(geometry && { geometry }),
          ...(radius !== undefined && { radius }), // Preserve radius for circles (can be 0, so check undefined)
          ...(width !== undefined && { width }), // Preserve width for rectangles (can be 0, so check undefined)
          ...(height !== undefined && { height }), // Preserve height for rectangles (can be 0, so check undefined)
          ...(shape && { shape }),
          ...(color && { color }),
          ...(textPopupInitialData?.markerImage != null && {
            markerImage: textPopupInitialData.markerImage
          })
        };

        console.log('📝 Annotations - Creating new annotation with params:', {
          type: annotationType,
          hasGeometry: !!params.geometry,
          hasRadius: params.radius !== undefined,
          hasWidth: params.width !== undefined,
          hasHeight: params.height !== undefined,
          hasShape: !!params.shape,
          hasColor: !!params.color,
          geometry: params.geometry,
          radius: params.radius,
          width: params.width,
          height: params.height,
          shape: params.shape,
          color: params.color
        });

        newAnnotation(
          {
            x: textPopupPosition.x,
            y: textPopupPosition.y,
            z: textPopupPosition.z
          },
          annotationType, // 🆕 Use preserved type instead of hardcoded 'Text'
          params
        );
        console.log('✅ Annotations - Created new annotation:', annotationType);
      }
      if (textPopupEndsShapeToolSession) {
        invokeShapeAnnotateToolbarSessionEnd?.();
      }
      setTextPopupPosition(null);
      setTextPopupInitialData(null);
      setTextPopupReadOnly(false);
      setTextPopupIsFromTask(false);
      setTextPopupOnTaskComplete(null);
      setTextPopupEndsShapeToolSession(false);
      setTextPopupOnCancel(null);
      setTextPopupHideTaskCheckbox(false);
    },
    [
      newAnnotation,
      textPopupPosition,
      textPopupInitialData,
      textPopupReadOnly,
      textPopupEndsShapeToolSession,
      invokeShapeAnnotateToolbarSessionEnd,
      setAnnotations,
      annotations
    ]
  );

  const handleTextAnnotationCancel = useCallback(() => {
    console.log('📝 Annotations - Text popup cancel', {
      hasOnCancel: !!textPopupOnCancel
    });

    // 🆕 If cancel callback is provided (for shape annotations), call it immediately
    if (textPopupOnCancel) {
      console.log('🗑️ Annotations - Calling cancel callback for shape annotation');
      textPopupOnCancel();
    }

    setTextPopupPosition(null);
    setTextPopupInitialData(null);
    setTextPopupReadOnly(false);
    setTextPopupIsFromTask(false);
    setTextPopupOnTaskComplete(null);
    setTextPopupOnCancel(null);
    setTextPopupHideTaskCheckbox(false);
    if (textPopupEndsShapeToolSession) {
      invokeShapeAnnotateToolbarSessionEnd?.();
    }
    setTextPopupEndsShapeToolSession(false);
  }, [textPopupOnCancel, textPopupEndsShapeToolSession, invokeShapeAnnotateToolbarSessionEnd]);

  // Check if this is a shape annotation (ShapeLine, ShapeCircle, ShapeRectangle, ShapeX)
  // For shape annotations, we want the popup's top-left corner at the end point
  const isShapeAnnotation =
    textPopupInitialData &&
    (textPopupInitialData.type === 'ShapeLine' ||
      textPopupInitialData.type === 'ShapeCircle' ||
      textPopupInitialData.type === 'ShapeRectangle' ||
      textPopupInitialData.type === 'ShapeX' ||
      textPopupInitialData.shape === 'line' ||
      textPopupInitialData.shape === 'circle' ||
      textPopupInitialData.shape === 'rectangle' ||
      textPopupInitialData.shape === 'x');

  return (
    <group>
      {/* 🆕 Text annotation popup */}
      {textPopupPosition && (
        <Html
          position={[textPopupPosition.x, textPopupPosition.y, textPopupPosition.z]}
          style={{ pointerEvents: 'auto' }}
          center={false}
          className={`text-annotation-popup-html ${isShapeAnnotation ? 'shape-annotation-popup' : ''}`} // 🔧 Add class for shape annotations
        >
          {/* Placement wrapper only — card chrome lives in TextAnnotationPopup */}
          <div
            className="text-annotation-popup-placement"
            style={{
              boxSizing: 'border-box',
              transform: `translate(${popupPlacement.horizontal === 'left' ? '-100%' : '0'}, ${popupPlacement.vertical === 'top' ? '-100%' : '0'})`
            }}
          >
            <TextAnnotationPopup
              position={textPopupPosition}
              onSubmit={handleTextAnnotationSubmit}
              onCancel={handleTextAnnotationCancel}
              userName={userName || 'User'}
              initialData={textPopupInitialData}
              readOnly={textPopupReadOnly}
              isFromTask={textPopupIsFromTask}
              onTaskComplete={textPopupOnTaskComplete}
              hideTaskCheckbox={textPopupHideTaskCheckbox}
              uploadAnnotationFile={uploadAnnotationFile}
              onOpenCameraCapture={(data) => {
                if (onOpenCameraCapture) {
                  onOpenCameraCapture({
                    ...data,
                    textPopupState:
                      textPopupPosition && data?.currentPopupState
                        ? {
                            position: textPopupPosition,
                            initialData: data.currentPopupState,
                            readOnly: textPopupReadOnly,
                            isFromTask: textPopupIsFromTask,
                            onTaskComplete: textPopupOnTaskComplete
                          }
                        : undefined,
                    closeTextPopup: () => {
                      closeTextPopup();
                    }
                  });
                }
              }}
              onOpenImageAnnotation={(data) => {
                // 🔧 Close text popup and pass state to reopen later
                if (onOpenImageAnnotation) {
                  // 🔧 Use currentPopupState from data if provided, otherwise fall back to initialData
                  const currentState = data.currentPopupState || textPopupInitialData;

                  // 🔧 Ensure all critical fields are present, including geometry/shape/color for ShapeLine annotations
                  const stateWithAllFields = currentState
                    ? {
                        ...currentState,
                        position: currentState.position || textPopupInitialData?.position || textPopupPosition,
                        type: currentState.type || textPopupInitialData?.type || 'Text',
                        id: currentState.id || textPopupInitialData?.id,
                        createdAt: currentState.createdAt || textPopupInitialData?.createdAt,
                        // 🆕 CRITICAL: Preserve geometry, shape, and color for ShapeLine annotations
                        // These fields are essential for rendering the line on the canvas
                        geometry: currentState.geometry || textPopupInitialData?.geometry,
                        shape: currentState.shape || textPopupInitialData?.shape,
                        color: currentState.color || textPopupInitialData?.color
                      }
                    : null;

                  onOpenImageAnnotation({
                    ...data,
                    textPopupState:
                      textPopupPosition && stateWithAllFields
                        ? {
                            position: textPopupPosition,
                            initialData: stateWithAllFields, // 🔧 Use current state with all fields
                            readOnly: textPopupReadOnly,
                            isFromTask: textPopupIsFromTask,
                            onTaskComplete: textPopupOnTaskComplete
                          }
                        : undefined,
                    closeTextPopup: () => {
                      closeTextPopup();
                    }
                  });
                  // Close the text popup
                  closeTextPopup();
                }
              }}
            />
          </div>
        </Html>
      )}

      {/* Hide all point annotation labels when text popup or image annotator is open so they don't overlap */}
      {!textPopupPosition &&
        !imageAnnotationModalOpen &&
        (accordion == 'annotations' || (iframeCallback && !designFrame)) &&
        annotations &&
        annotations
          .filter((annotation) => {
            if (!('x' in annotation.position)) return false;
            // Mesh shapes from CanvasShapeAnnotations — exclude from Html layer
            if (
              annotation.type === 'ShapeLine' ||
              annotation.shape === 'line' ||
              annotation.type === 'ShapeCircle' ||
              annotation.shape === 'circle' ||
              annotation.type === 'ShapeRectangle' ||
              annotation.shape === 'rectangle'
            ) {
              return false;
            }
            // ShapeX: mesh lines only when no markerImage; billboard markers use Html like quick markers
            const isMeshCross = (annotation.type === 'ShapeX' || annotation.shape === 'x') && !annotation.markerImage;
            if (isMeshCross) return false;
            return true;
          })
          .map((annotation) => {
            const isShapeXBillboard = annotation.type === 'ShapeX' || annotation.shape === 'x';
            const isPlainTextPin = annotation.type === 'Text' && !annotation.markerImage;
            const pinImageSrc = annotation.markerImage
              ? annotation.markerImage
              : isPlainTextPin
                ? TEXT_ANNOTATION_MARKER_IMAGE_DATA_URL
                : '/annotation_place.png';
            const pinSize = isShapeXBillboard ? SHAPE_X_MARKER_IMG_PX : isPlainTextPin ? TEXT_ANNOTATION_MARKER_IMG_PX : 24;
            const hasCustomPinImage = Boolean(annotation.markerImage);
            const showTypeIconBesideLabel = !hasCustomPinImage && !isPlainTextPin;
            const showCaptionText = annotation.type !== 'Text' && !isShapeXBillboard;
            const isDeleteMode = mode === 'delete' && !readonly;
            const overlayPointerEvents = isDeleteMode ? 'auto' : 'none';
            const handleOverlayDelete = (e) => {
              if (!isDeleteMode) return;
              e.preventDefault();
              e.stopPropagation();
              if (!setAnnotations) return;
              console.log('🗑️ Annotations - Delete mode: removing annotation from overlay click', annotation.id);
              setAnnotations(filterOutAnnotation(annotations, annotation));
            };
            return (
              <Html
                key={'annotate_' + (annotation.id || annotation.text)}
                position={[annotation.position.x, annotation.position.y, annotation.position.z]}
                style={{ pointerEvents: overlayPointerEvents }}
              >
                <div
                  style={{
                    position: 'relative',
                    top: '-30px',
                    pointerEvents: overlayPointerEvents,
                    cursor: isDeleteMode ? 'pointer' : undefined
                  }}
                  tabIndex={-1}
                  onPointerDown={isDeleteMode ? handleOverlayDelete : undefined}
                >
                  <img
                    src={pinImageSrc}
                    alt=""
                    style={{
                      position: 'relative',
                      left: isShapeXBillboard ? '-16px' : '-9px',
                      top: isShapeXBillboard ? '28px' : '16px',
                      pointerEvents: overlayPointerEvents,
                      objectFit: hasCustomPinImage || isPlainTextPin ? 'contain' : undefined,
                      zIndex: 2
                    }}
                    width={pinSize}
                    height={pinSize}
                    tabIndex={-1}
                  />
                  <div className="line" tabIndex={-1} style={{ pointerEvents: overlayPointerEvents, zIndex: 0 }}></div>
                  {showCaptionText ? (
                    <div
                      className="annotation"
                      style={{
                        whiteSpace: 'pre',
                        pointerEvents: overlayPointerEvents,
                        position: 'relative',
                        zIndex: 1,
                        background: 'transparent',
                        padding: 0,
                        color: 'inherit',
                        textShadow: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        lineHeight: 1.25,
                        boxSizing: 'border-box',
                        marginLeft: hasCustomPinImage || isPlainTextPin ? 8 : 6
                      }}
                      tabIndex={-1}
                    >
                      {showTypeIconBesideLabel ? getIcon(annotation.type) : null}
                      <span
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          padding: '2px 6px',
                          borderRadius: 4,
                          color: '#111',
                          textShadow: 'none',
                          pointerEvents: overlayPointerEvents
                        }}
                      >
                        {annotation.text}
                      </span>
                    </div>
                  ) : null}
                </div>
              </Html>
            );
          })}
    </group>
  );
};

export default Annotations;
